/**
 * schema-form-unicode-regex (2026-10-29) — how the dashboard's form reads a
 * string the way the chokepoint does: the ONE place it compiles a JSON-Schema
 * `pattern`, and the length it measures for `minLength` / `maxLength`.
 *
 * The chokepoint validates every input with Ajv (`CHOKEPOINT_AJV_OPTIONS`,
 * `packages/canonical-actions/src/registry.ts`). Two Ajv defaults decide what
 * a string means there, and the form must read the same way or it refuses
 * what the server accepts:
 *
 *   - `unicodeRegExp`: every `pattern` is compiled with the `u` flag. Without
 *     it a negated class such as `[^\u0000\uD800-\uDFFF]` is tested one UTF-16
 *     code unit at a time, so an emoji — a surrogate PAIR — lands in the
 *     surrogate range and the field says "Doesn't match pattern" for a value
 *     the chokepoint stores (the `/status` run form, 29 Oct 2026); and `\p{…}`
 *     is a plain `p` rather than a property class. Under `u` the class is
 *     tested per code point: an emoji passes and only a LONE surrogate is
 *     refused, exactly as at the chokepoint.
 *   - `unicode`: `minLength` and `maxLength` count code points, so an emoji
 *     counts once. `String.prototype.length` counts UTF-16 code units and
 *     counts it twice — refusing, near a limit, a value the server accepts.
 *
 * `packages/canonical-actions/tests/input-schema-pattern-dialect.test.ts`
 * pins both facts at the chokepoint; `apps/dashboard/tests/
 * schema-form-pattern.test.ts` pins that this module is the form's only
 * pattern compiler and length, and that every registered pattern compiles
 * through it.
 *
 * The browser's own `<input pattern>` check is no substitute: HTML compiles
 * that attribute with the `v` flag, under which a class with an unescaped
 * `-`, `/`, `{` or `}` (`[a-z0-9-]`) is not valid, so the browser ignores the
 * attribute — on 2026-10-29 for 170 of the registry's 592 patterns. This
 * module is the client-side check that runs.
 *
 * Shared by the backend tree and the SPA through the
 * `@layer/dashboard/schema-form/pattern` alias, like `compiler.ts` and
 * `defaults.ts`: pure, no deps.
 */

/** The flags the chokepoint's Ajv compiles every `pattern` with (its `unicodeRegExp` default). */
export const SCHEMA_PATTERN_FLAGS = "u";

/**
 * Compile a schema `pattern` in the chokepoint's dialect, or null when it
 * does not compile — the form then skips its own check and the server's
 * answer stands. Every registered pattern compiles; the null arm is for a
 * schema the dashboard has not seen before.
 */
export function compileSchemaPattern(pattern: string): RegExp | null {
  try {
    return new RegExp(pattern, SCHEMA_PATTERN_FLAGS);
  } catch {
    return null;
  }
}

/**
 * A string's length as the chokepoint measures it for `minLength` /
 * `maxLength`: code points, a surrogate PAIR counting once (Ajv's `unicode`
 * default, its `ucs2length`). A lone surrogate counts once too, as there.
 */
export function schemaStringLength(value: string): number {
  let length = 0;
  for (let i = 0; i < value.length; i += 1) {
    const unit = value.charCodeAt(i);
    if (unit >= 0xd800 && unit <= 0xdbff && i + 1 < value.length) {
      const next = value.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) i += 1;
    }
    length += 1;
  }
  return length;
}
